import { useQuery } from '@apollo/client/react';

import { CurrentUserDocument } from 'graphql/types';
import { TFrontendRole } from 'user/types';

export enum ObjectAction {
    read = 'read',
    create = 'create',
    update = 'update',
    delete = 'delete',
    resetProgress = 'resetProgress',
}

type IRoleObjectPermissions = { [key in ObjectAction]: TFrontendRole[] };
type IRolePermissions = { [key: string]: Partial<IRoleObjectPermissions> };

const permissions: IRolePermissions = {
    dashboard: {
        read: [TFrontendRole.Manager],
    },
    libraryCollection: {
        create: [TFrontendRole.Author, TFrontendRole.Manager],
        update: [TFrontendRole.Author, TFrontendRole.Manager],
        delete: [TFrontendRole.Author, TFrontendRole.Manager],
    },
    libraryArticle: {
        create: [TFrontendRole.Author, TFrontendRole.Manager],
        update: [TFrontendRole.Author, TFrontendRole.Manager],
        delete: [TFrontendRole.Author, TFrontendRole.Manager],
    },
    training: {
        create: [
            TFrontendRole.Manager,
            TFrontendRole.Planner,
            TFrontendRole.Author,
        ],
        update: [TFrontendRole.Manager, TFrontendRole.Planner],
        delete: [TFrontendRole.Manager, TFrontendRole.Planner],
    },
    organisation: {
        update: [TFrontendRole.Manager, TFrontendRole.Owner],
        delete: [TFrontendRole.Owner],
    },
    offer: {
        create: [TFrontendRole.Author, TFrontendRole.Manager],
        update: [TFrontendRole.Author, TFrontendRole.Manager],
        delete: [TFrontendRole.Author, TFrontendRole.Manager],
    },
    helpdesk: {
        read: [
            TFrontendRole.Owner,
            TFrontendRole.Manager,
            TFrontendRole.Planner,
        ],
    },
    portfolioSettings: {
        update: [TFrontendRole.Owner, TFrontendRole.Manager],
    },
    organisationSettings: {
        update: [TFrontendRole.Manager],
    },
    video: {
        create: [TFrontendRole.Author, TFrontendRole.Manager],
        update: [TFrontendRole.Author, TFrontendRole.Manager],
        delete: [TFrontendRole.Author, TFrontendRole.Manager],
    },
    group: {
        create: [
            TFrontendRole.Manager,
            TFrontendRole.Owner,
            TFrontendRole.Planner,
        ],
        update: [
            TFrontendRole.Manager,
            TFrontendRole.Owner,
            TFrontendRole.Planner,
        ],
        delete: [
            TFrontendRole.Manager,
            TFrontendRole.Owner,
            TFrontendRole.Planner,
        ],
    },
    course: {
        create: [TFrontendRole.Author, TFrontendRole.Manager],
        update: [TFrontendRole.Author, TFrontendRole.Manager],
        delete: [TFrontendRole.Author, TFrontendRole.Manager],
    },
    user: {
        create: [TFrontendRole.Manager],
        update: [TFrontendRole.Manager],
        delete: [TFrontendRole.Manager],
    },
    userManagement: {
        create: [TFrontendRole.Owner, TFrontendRole.Manager],
        update: [
            TFrontendRole.Owner,
            TFrontendRole.Manager,
            TFrontendRole.GroupManager,
            TFrontendRole.Trainer,
        ],
        delete: [
            TFrontendRole.Owner,
            TFrontendRole.Manager,
            TFrontendRole.GroupManager,
            TFrontendRole.Author,
        ],
    },
    enrollmentsManagement: {
        read: [
            TFrontendRole.Owner,
            TFrontendRole.Manager,
            TFrontendRole.Author,
            TFrontendRole.Trainer,
            TFrontendRole.GroupManager,
        ],
    },
    trainingParticipants: {
        create: [TFrontendRole.Owner, TFrontendRole.Manager],
        update: [TFrontendRole.Owner, TFrontendRole.Manager],
        delete: [TFrontendRole.Owner, TFrontendRole.Manager],
    },
    generateCertificate: {
        create: [TFrontendRole.Manager],
    },
    portfolioItem: {
        resetProgress: [
            TFrontendRole.Owner,
            TFrontendRole.Manager,
            TFrontendRole.Trainer,
        ],
    },
};

export type RoleObjectType = keyof typeof permissions;

// Utility type to generate canX keys from ObjectAction values
type PermissionKey<A extends string> = `can${Capitalize<A>}`;
type AllPermissions = {
    [K in ObjectAction as PermissionKey<K>]: boolean;
};

export const useFrontendPermissions = (
    object?: RoleObjectType
): AllPermissions & { userRoles: TFrontendRole[] | null; loading: boolean } => {
    const { data: user, loading } = useQuery(CurrentUserDocument);

    const allActions = Object.values(ObjectAction);

    // When no object is given we want to return all false for all permissions.
    if (!object) {
        const result = Object.fromEntries(
            allActions.map((action) => [
                `can${action.charAt(0).toUpperCase()}${action.slice(1)}`,
                false,
            ])
        ) as AllPermissions;

        return {
            ...result,
            userRoles: null,
            loading: false,
        };
    }

    const { roles, isGroupManager = false } = user?.currentUser || {};
    const userRoles = ((roles || []) as (TFrontendRole | null)[]).filter(
        Boolean
    );

    if (isGroupManager) {
        userRoles.push(TFrontendRole.GroupManager);
    }

    const objectPermissions = permissions[object] || {};
    const result = Object.fromEntries(
        allActions.map((action) => [
            `can${action.charAt(0).toUpperCase()}${action.slice(1)}`,
            !!objectPermissions[action as ObjectAction]?.some(
                (role: TFrontendRole) => userRoles.includes(role)
            ),
        ])
    ) as AllPermissions;

    return {
        ...result,
        userRoles,
        loading,
    };
};
